import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { of } from 'rxjs';

export interface SessionProfile {
  uid: string;
  role?: string;
  isApproved?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly profileSubject = new BehaviorSubject<
    SessionProfile | null | undefined
  >(undefined);

  readonly profile$ = this.profileSubject.asObservable();

  constructor(
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore,
  ) {
    this.afAuth.authState
      .pipe(
        switchMap((user) => {
          if (!user) {
            return of(null);
          }
          return this.afs
            .collection('users')
            .doc(user.uid)
            .valueChanges()
            .pipe(
              map((data: any) => {
                const current = this.profileSubject.value;
                const sameUser = !!(current && current.uid === user.uid);
                // Prefer Firestore, but keep a remembered profile while the
                // users/{uid} doc is still being written (signup race).
                return {
                  uid: user.uid,
                  role: data?.role ?? (sameUser ? current!.role : undefined),
                  isApproved:
                    typeof data?.isApproved === 'boolean'
                      ? data.isApproved
                      : sameUser
                        ? current!.isApproved
                        : undefined,
                };
              }),
            );
        }),
      )
      .subscribe((profile) => this.profileSubject.next(profile));
  }

  remember(profile: SessionProfile): void {
    this.profileSubject.next(profile);
  }

  clear(): void {
    this.profileSubject.next(null);
  }

  /** Waits until we know whether someone is signed in. */
  ready(): Observable<SessionProfile | null> {
    return this.profileSubject.pipe(
      filter((profile): profile is SessionProfile | null => profile !== undefined),
      take(1),
    );
  }
}
